import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Collapse, Descriptions, Space, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import GcPagina from '../Layout/GcPagina';
import {
  darPorTratado,
  ESTADO_TRATADO,
  leerAccionesCaso,
  leerActividadCliente,
  leerLecturaCaso,
  mensajeError,
  reabrirCaso,
  RECURSO_OPORTUNIDADES,
} from '../../api/feriasApi';
import type { AccionesCaso } from '../../api/feriasApi';
import { usePermissions } from '../../contexts/PermissionContext';
import { FctAviso, FctEstado } from '../../fct/react';
import { Generado } from './Generado';
import { AnadirActividad } from './AnadirActividad';
import { motivoEnPantalla } from './motivos';
import { OportunidadCaso } from './OportunidadCaso';
import { TratamientoCaso } from './TratamientoCaso';
import type { DatosTratamiento } from './TratamientoCaso';
import { dia, SIN_DATO } from './textos';
import './Ferias.css';

// Las tres secciones, abiertas al entrar (M6 §3, P1).
const SECCIONES = ['tratamiento', 'venta', 'compra'];

// El cliente del caso como lo lee el supervisor: código y nombre de Gescom, o su número si Gescom no está.
function clienteDe(caso: AccionesCaso): string {
  const codigo = caso.codigoCliente || `nº ${caso.idCliente}`;
  return caso.nombreCliente ? `${codigo} · ${caso.nombreCliente}` : codigo;
}

/**
 * La pantalla «Acciones» de un caso (07_MODULOS/FERIAS/M6_ACCIONES.md): una cabecera con el resumen del
 * caso, tres secciones plegables (Tratamiento, Oportunidad de venta y Oportunidad de compra) y, al pie,
 * «Dar por tratado» o «Reabrir» (D-FE-38, P6). Solo se trata un caso con cliente; uno sin él, abierto por
 * su dirección, dice por qué y lleva a su cliente. Las secciones de oportunidad solo muestran su contenido
 * a quien tiene también Oportunidades.Gestionar (D-FE-39). Tratamiento muestra las indicaciones de la
 * ficha y la actividad reciente del cliente (#136); cada sección de oportunidad, la generada o cómo
 * generarla (#138); Tratamiento, además, añade actividades (#137).
 */
const AccionesCasoPagina: React.FC = () => {
  const idCaso = Number(useParams().idCaso);
  const navegar = useNavigate();
  const { hasPermission } = usePermissions();
  const puedeOportunidades = hasPermission(RECURSO_OPORTUNIDADES);
  const [caso, setCaso] = useState<AccionesCaso | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorEstado, setErrorEstado] = useState<string | null>(null);
  const [cambiando, setCambiando] = useState(false);
  // Un cambio de estado a la vez: un doble clic no manda dos peticiones.
  const enCurso = useRef(false);
  // Lo verificado de la ficha y la actividad del cliente: los usan Tratamiento y las oportunidades.
  const [datos, setDatos] = useState<DatosTratamiento>({ valores: undefined, errorFicha: null, actividad: null, errorActividad: null });

  // La cabecera del caso, al entrar y al cambiar de caso.
  useEffect(() => {
    let vigente = true;
    setCaso(null);
    setError(null);
    setDatos({ valores: undefined, errorFicha: null, actividad: null, errorActividad: null });
    leerAccionesCaso(idCaso)
      .then((leido) => vigente && setCaso(leido))
      .catch((e: unknown) => vigente && setError(motivoEnPantalla(mensajeError(e))));
    leerLecturaCaso(idCaso)
      .then((lectura) => vigente && setDatos((d) => ({ ...d, valores: lectura.verificacion?.valores ?? null })))
      .catch((e: unknown) => vigente && setDatos((d) => ({ ...d, errorFicha: motivoEnPantalla(mensajeError(e)) })));
    leerActividadCliente(idCaso)
      .then((actividad) => vigente && setDatos((d) => ({ ...d, actividad })))
      .catch((e: unknown) => vigente && setDatos((d) => ({ ...d, errorActividad: motivoEnPantalla(mensajeError(e)) })));
    return () => {
      vigente = false;
    };
  }, [idCaso]);

  // Tras añadir una actividad: la lista del cliente la muestra, y la cabecera, su paso completado.
  const trasAnadirActividad = () => {
    leerActividadCliente(idCaso)
      .then((actividad) => setDatos((d) => ({ ...d, actividad, errorActividad: null })))
      .catch((e: unknown) => setDatos((d) => ({ ...d, errorActividad: motivoEnPantalla(mensajeError(e)) })));
    leerAccionesCaso(idCaso)
      .then(setCaso)
      .catch(() => undefined);
  };

  // «Dar por tratado» o «Reabrir»: la API devuelve la cabecera ya cambiada.
  const cambiarEstado = async (cambio: (id: number) => Promise<AccionesCaso>) => {
    if (enCurso.current) {
      return;
    }
    enCurso.current = true;
    setCambiando(true);
    setErrorEstado(null);
    try {
      setCaso(await cambio(idCaso));
    } catch (e) {
      setErrorEstado(motivoEnPantalla(mensajeError(e)));
    } finally {
      enCurso.current = false;
      setCambiando(false);
    }
  };

  const pagina = (contenido: ReactNode) => (
    <GcPagina
      seccion={['Ferias', 'Bandeja']}
      titulo={`Acciones de la ficha #${idCaso}`}
      contexto={caso ? `${caso.feria} · ${caso.estado}` : undefined}
      acciones={
        <Space>
          <Button onClick={() => navegar('/ferias')}>Volver a la bandeja</Button>
          <Button onClick={() => navegar(`/ferias/casos/${idCaso}/cliente`)}>Ver el cliente</Button>
        </Space>
      }
    >
      {error && (
        <FctAviso tipo="error" titulo="No se ha podido abrir la ficha">
          {error}
        </FctAviso>
      )}
      {contenido}
    </GcPagina>
  );

  if (!caso) {
    return pagina(!error && <Spin aria-label="Cargando las acciones del caso" />);
  }

  // M6 §2: sin cliente no hay nada que tratar; el paso de cliente es lo siguiente.
  if (caso.idCliente === null) {
    return pagina(
      <FctAviso tipo="aviso" titulo="Esta ficha todavía no tiene cliente">
        Las acciones se hacen sobre el cliente de Gescom de la ficha. Guarde antes su cliente.{' '}
        <Button type="link" style={{ padding: 0 }} onClick={() => navegar(`/ferias/casos/${idCaso}/cliente`)}>
          Ir al cliente
        </Button>
      </FctAviso>,
    );
  }

  // D-FE-39: sin Oportunidades.Gestionar, la sección dice por qué no muestra nada.
  const oportunidad = (tipo: 'venta' | 'compra') =>
    puedeOportunidades ? (
      <OportunidadCaso tipo={tipo} caso={caso} valores={datos.valores ?? null} actividad={datos.actividad} onCaso={setCaso} />
    ) : (
      <FctAviso tipo="info" titulo={`No puede generar oportunidades de ${tipo}`}>
        Generar una oportunidad exige el permiso Oportunidades.Gestionar, que su usuario no tiene. Si debe generarlas, pídalo a Sistemas.
      </FctAviso>
    );

  const tratado = caso.idEstado === ESTADO_TRATADO;

  return pagina(
    <>
      <Descriptions size="small" column={3} bordered aria-label="Resumen del caso" className="ferias-acciones__cabecera">
        <Descriptions.Item label="Feria">{caso.feria}</Descriptions.Item>
        <Descriptions.Item label="Fecha de la reunión">{dia(caso.fechaReunion)}</Descriptions.Item>
        <Descriptions.Item label="Cliente">{clienteDe(caso)}</Descriptions.Item>
        <Descriptions.Item label="Aviso">{caso.aviso ?? SIN_DATO}</Descriptions.Item>
        <Descriptions.Item label="Prioridad">{caso.prioridad === null ? SIN_DATO : caso.prioridad ? 'Prioritaria' : 'No'}</Descriptions.Item>
        <Descriptions.Item label="Estado">
          <Space>
            <FctEstado personalizado={{ etiqueta: caso.estado, familia: tratado ? 'exito' : 'neutro' }} />
            <Generado avance={caso.avance} />
          </Space>
        </Descriptions.Item>
      </Descriptions>

      <Collapse
        className="ferias-acciones__secciones"
        defaultActiveKey={SECCIONES}
        items={[
          {
            key: 'tratamiento',
            label: 'Tratamiento',
            children: (
              <TratamientoCaso {...datos} anadir={<AnadirActividad idCaso={idCaso} cliente={clienteDe(caso)} onCreada={trasAnadirActividad} />} />
            ),
          },
          { key: 'venta', label: 'Oportunidad de venta', children: oportunidad('venta') },
          { key: 'compra', label: 'Oportunidad de compra', children: oportunidad('compra') },
        ]}
      />

      <div className="ferias-acciones__pie">
        {errorEstado && (
          <FctAviso tipo="error" titulo="No se ha cambiado el estado del caso">
            {errorEstado}
          </FctAviso>
        )}
        {tratado ? (
          <Space>
            <span>El caso está tratado.</span>
            <Button loading={cambiando} onClick={() => void cambiarEstado(reabrirCaso)}>
              Reabrir
            </Button>
          </Space>
        ) : (
          <Button type="primary" loading={cambiando} onClick={() => void cambiarEstado(darPorTratado)}>
            Dar por tratado
          </Button>
        )}
      </div>
    </>,
  );
};

export default AccionesCasoPagina;
